import store from '@/store/store';
import {
  DataSet,
  DataSetMetadata,
  DataSetRule,
  PublishingIntegrationConfiguration,
  IdResponse,
  Integration,
  IntegrationConfiguration,
  StreamingIntegrationConfiguration,
  SuccessResponse,
} from '@/types';
import { ApiBaseService, IntegrationsServiceAPI, DataServiceAPI } from '@/services/api.base.service';
import 'url-search-params-polyfill';

export class DataSetService {
  private publish: ApiBaseService;
  private dataServiceApi: ApiBaseService;

  constructor() {
    this.publish = IntegrationsServiceAPI;
    this.dataServiceApi = DataServiceAPI;
  }

  public async getDataSets(includeInputDatasets: boolean = false): Promise<DataSet[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const params = includeInputDatasets ? {} : { type: 'output' };
    return this.publish.get(`organisation/${organisationId}/datasets`, { params });
  }

  public async getDataSetById(id: number): Promise<DataSet> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.get(`organisation/${organisationId}/datasets/${id}`);
  }

  public async createDataSet(payload: DataSet): Promise<DataSet> {
    const organisationId = store.getters.activeOrganisation[0].id;
    payload.organisation_slug = store.getters.activeOrganisation[0].slug;
    return this.publish.post(`organisation/${organisationId}/datasets/`, payload);
  }

  public async updateDataSet(id: number, payload: DataSet): Promise<DataSet> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.put(`organisation/${organisationId}/datasets/${id}`, payload);
  }

  public async deleteDataSet(id: number): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.delete(`organisation/${organisationId}/datasets/${id}`);
  }

  public async getStreamingIntegrationsForDataset( id: number): Promise<Integration[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.get(`organisation/${organisationId}/datasets/${id}/integrations/streaming`);
  }
  public async getPublishingIntegrationsForDataset( id: number): Promise<IntegrationConfiguration[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.get(`organisation/${organisationId}/datasets/${id}/integrations/publishing`);
  }

  public async getIntegrationsForAllDatasets(datasetIds: number[]): Promise<{ [k: string]: IntegrationConfiguration[] }> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const queryString = datasetIds.reduce((qs, id) => {
      if (qs === '') {
        return `[${id.toString()}`;
      } else {
        return `${qs},${id.toString()}`;
      }
    }, '') + ']';
    return this.publish.get(`organisation/${organisationId}/datasets/${queryString}/integrations/all`);
  }

  public async getIntegrationCountsForDataset(datasetIds: number[]): Promise<any> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const datasetIdString = datasetIds.join(',');
    return this.publish.get(`organisation/${organisationId}/datasets/integrations/counts`, { params: { datasetIds: datasetIdString } });
  }

  public async createStreamingIntegrationForDataset( datasetId: number, body: Partial<StreamingIntegrationConfiguration>): Promise<IntegrationConfiguration> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.post(`organisation/${organisationId}/datasets/${datasetId}/integrations/streaming`, body);
  }

  public async updateStreamingIntegrationForDataset( datasetId: number, integrationId: number, body: Partial<StreamingIntegrationConfiguration>): Promise<IntegrationConfiguration> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.put(`organisation/${organisationId}/datasets/${datasetId}/integrations/streaming/${integrationId}`, body);
  }

  public async createPublishingIntegrationForDataset( datasetId: number, body: Partial<PublishingIntegrationConfiguration>): Promise<PublishingIntegrationConfiguration> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.post(`organisation/${organisationId}/datasets/${datasetId}/integrations/publishing`, body);
  }

  public async updatePublishingIntegrationForDataset( datasetId: number, integrationId: number, body: Partial<PublishingIntegrationConfiguration>): Promise<PublishingIntegrationConfiguration> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.put(`organisation/${organisationId}/datasets/${datasetId}/integrations/publishing/${integrationId}`, body);
  }

  public async deleteIntegrationForDataset(datasetId: number, integrationId: number): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.delete(`organisation/${organisationId}/datasets/${datasetId}/integrations/${integrationId}`);
  }

  public async deleteAllIntegrationsForDataset(datasetId: number): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.delete(`organisation/${organisationId}/datasets/${datasetId}/integrations`);
  }

  public async getDatasetRules(datasetId: number): Promise<DataSetRule[]> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.get(`organisation/${organisationId}/datasets/${datasetId}/rules`);
  }

  public async getRulesForAllDatasets(datasetIds: number[]): Promise<{ [k: string]: DataSetRule[] }> {
    const organisationId = store.getters.activeOrganisation[0].id;
    const queryString = datasetIds.reduce((qs, id) => {
      if (qs === '') {
        return `[${id.toString()}`;
      } else {
        return `${qs},${id.toString()}`;
      }
    }, '') + ']';
    return this.publish.get(`organisation/${organisationId}/datasets/${queryString}/rules/all`);
  }

  public async createDatasetRule(datasetId: number, body: DataSetRule): Promise<IdResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.post(`organisation/${organisationId}/datasets/${datasetId}/rules`, body);
  }

  public async updateDatasetRule(datasetId: number, ruleId: number, body: DataSetRule): Promise<IdResponse>  {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.put(`organisation/${organisationId}/datasets/${datasetId}/rules/${ruleId}`, body);
  }

  public async deleteDatasetRule(datasetId: number, ruleId: number): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.delete(`organisation/${organisationId}/datasets/${datasetId}/rules/${ruleId}`);
  }

  public async deleteAllRulesForDataset(datasetId: number): Promise<SuccessResponse> {
    const organisationId = store.getters.activeOrganisation[0].id;
    return this.publish.delete(`organisation/${organisationId}/datasets/${datasetId}/rules`);
  }

  public async getDatasetData(uuid: string, options: {
    columns?: string,
    tags?: { [key: string]: string[] },
    limit?: number;
    offset?: number;
    since?: Date | number | string;
    before?: Date | number | string;
    timeOrder?: 'asc' | 'desc';
    includeTestBench: boolean;
    includeStock: boolean;
  } = { includeStock: false, includeTestBench: false }): Promise<{ results: any[], fields: string[]}> {
    const queryParams = new URLSearchParams();
    queryParams.append('includeAllColumns', 'true');
    let queryString = '';
    if (options) {
      if (options.columns) {
        queryParams.append('columns', options.columns);
      }
      if (options.tags && Object.keys(options.tags).length) {
        for (const tag in options.tags) {
          if (options.tags.hasOwnProperty(tag)) {
            queryParams.append(tag, options.tags[tag].join(','));
          }
        }
      }
      if (options.limit) {
        queryParams.append('limit', options.limit.toString());
      }
      if (options.offset) {
        queryParams.append('offset', options.offset.toString());
      }
      if (options.since) {
        if (typeof options.since === 'number') {
          queryParams.append('since', (options.since as number).toString());
        } else {
          queryParams.append('since', (options.since as Date).valueOf().toString());
        }
      }
      if (options.before) {
        if (typeof options.before === 'number') {
          queryParams.append('before', (options.before as number).toString());
        } else {
          queryParams.append('before', (options.before as Date).valueOf().toString());
        }
      }
      if (options.timeOrder) {
        queryParams.append('timeOrder', options.timeOrder);
      }

      queryParams.append('includeTestBench', (options.includeTestBench).toString());
      queryParams.append('includeStock', (options.includeStock).toString());

      queryString = `?${queryParams.toString()}`;
    }
    const orgSlug = store.getters.activeOrganisation[0].slug;
    return this.dataServiceApi.get(`datasets/${orgSlug}/${uuid}${queryString}`);
  }

  public async getDatasetMetadata(uuid: string): Promise<DataSetMetadata> {
    const orgSlug = store.getters.activeOrganisation[0].slug;
    return this.dataServiceApi.get(`datasets/${orgSlug}/${uuid}/metadata`);
  }
}

export default new DataSetService();
